import { useState, useEffect, useCallback } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import {
  Search,
  Download,
  Refresh,
  Description,
  PlayArrow,
  Stop,
  FirstPage,
  LastPage,
  FolderOpen,
  Close,
} from '@mui/icons-material'
import { logsApi } from '@api/endpoints/logs'
import type {
  LogFileDto,
  LogLineDto,
} from '@api/types/logs'

type ViewMode = 'paginated' | 'tail'
type SortField = 'fileName' | 'sizeBytes' | 'lastModifiedUtc' | 'createdUtc'
type SortOrder = 'asc' | 'desc'

const DRAWER_WIDTH = 600

export default function LogsPage() {
  usePageTitle('Logs')
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(true)

  // File list state
  const [files, setFiles] = useState<LogFileDto[]>([])
  const [filesLoading, setFilesLoading] = useState(true)
  const [filesError, setFilesError] = useState<string | null>(null)
  const [sortField, setSortField] = useState<SortField>('lastModifiedUtc')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Selected file state
  const [selectedFile, setSelectedFile] = useState<string | null>(null)

  // Content state
  const [content, setContent] = useState<LogLineDto[]>([])
  const [contentLoading, setContentLoading] = useState(false)
  const [contentError, setContentError] = useState<string | null>(null)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalLines, setTotalLines] = useState(0)

  // Search state
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('paginated')
  const [tailLines, setTailLines] = useState(50)
  const [autoRefresh, setAutoRefresh] = useState(false)

  // Load file list
  const loadFiles = useCallback(async () => {
    setFilesLoading(true)
    setFilesError(null)
    try {
      const response = await logsApi.getLogFiles()
      // Handle both array response and wrapped response
      const fileList = Array.isArray(response) ? response : (response?.files || [])
      setFiles(fileList)
    } catch (err) {
      console.error('Failed to load log files:', err)
      setFilesError(err instanceof Error ? err.message : 'Failed to load log files')
    } finally {
      setFilesLoading(false)
    }
  }, [])

  // Load file content (paginated)
  const loadContent = useCallback(async () => {
    if (!selectedFile) return
    setContentLoading(true)
    setContentError(null)
    try {
      const response = await logsApi.getLogFileContent(selectedFile, {
        page,
        pageSize,
        searchTerm: searchTerm || undefined,
      })
      setContent(response?.lines || [])
      setTotalPages(response?.totalPages || 0)
      setTotalLines(response?.totalLines || 0)
    } catch (err) {
      console.error('Failed to load log content:', err)
      setContentError(err instanceof Error ? err.message : 'Failed to load log content')
    } finally {
      setContentLoading(false)
    }
  }, [selectedFile, page, pageSize, searchTerm])

  // Load file tail
  const loadTail = useCallback(async () => {
    if (!selectedFile) return
    setContentLoading(true)
    setContentError(null)
    try {
      const response = await logsApi.getLogFileTail(selectedFile, {
        lines: tailLines,
      })
      setContent(response?.lines || [])
      setTotalLines(response?.totalLinesReturned || 0)
      setTotalPages(1)
    } catch (err) {
      console.error('Failed to load log tail:', err)
      setContentError(err instanceof Error ? err.message : 'Failed to load log tail')
    } finally {
      setContentLoading(false)
    }
  }, [selectedFile, tailLines])

  // Initial load of files
  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  // Load content when file or params change
  useEffect(() => {
    if (selectedFile) {
      if (viewMode === 'paginated') {
        loadContent()
      } else {
        loadTail()
      }
    }
  }, [selectedFile, viewMode, loadContent, loadTail])

  // Auto-refresh for tail mode
  useEffect(() => {
    if (autoRefresh && viewMode === 'tail' && selectedFile) {
      const interval = setInterval(loadTail, 3000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, viewMode, selectedFile, loadTail])

  // Sort files
  const sortedFiles = [...files].sort((a, b) => {
    let comparison = 0
    switch (sortField) {
      case 'fileName':
        comparison = a.fileName.localeCompare(b.fileName)
        break
      case 'sizeBytes':
        comparison = a.sizeBytes - b.sizeBytes
        break
      case 'lastModifiedUtc':
        comparison = new Date(a.lastModifiedUtc).getTime() - new Date(b.lastModifiedUtc).getTime()
        break
      case 'createdUtc':
        comparison = new Date(a.createdUtc).getTime() - new Date(b.createdUtc).getTime()
        break
    }
    return sortOrder === 'asc' ? comparison : -comparison
  })

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  // Handle file selection
  const handleFileSelect = (fileName: string) => {
    setSelectedFile(fileName)
    setPage(1)
    setSearchTerm('')
    setSearchInput('')
    setContent([])
    setDrawerOpen(false)
  }

  // Handle search
  const handleSearch = () => {
    setSearchTerm(searchInput)
    setPage(1)
  }

  // Handle search on Enter key
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  // Handle download
  const handleDownload = async () => {
    if (!selectedFile) return
    try {
      await logsApi.downloadLogFile(selectedFile)
    } catch (err) {
      console.error('Failed to download log file:', err)
    }
  }

  // Handle view mode change
  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode) {
      setViewMode(newMode)
      setPage(1)
    }
  }

  // Get background color for log line
  const getLineBackground = (line: string): string => {
    const lowerLine = line.toLowerCase()
    if (lowerLine.includes('[err]') || lowerLine.includes('[error]') || lowerLine.includes('[fatal]') || lowerLine.includes('[ftl]')) {
      return 'rgba(244, 67, 54, 0.08)'
    }
    if (lowerLine.includes('[wrn]') || lowerLine.includes('[warn]') || lowerLine.includes('[warning]')) {
      return 'rgba(255, 152, 0, 0.08)'
    }
    return 'transparent'
  }

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString()
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* File List Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant="temporary"
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            maxWidth: '90vw',
            mt: '64px',
            height: 'calc(100vh - 64px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Log Files</Typography>
              <Typography variant="body2" color="text.secondary">
                {files.length} file{files.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh file list">
                <IconButton size="small" onClick={loadFiles} disabled={filesLoading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          {/* File Table */}
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            {filesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filesError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {filesError}
              </Alert>
            ) : files.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No log files found</Typography>
              </Box>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'fileName'}
                        direction={sortField === 'fileName' ? sortOrder : 'asc'}
                        onClick={() => handleSort('fileName')}
                      >
                        File Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortField === 'sizeBytes'}
                        direction={sortField === 'sizeBytes' ? sortOrder : 'asc'}
                        onClick={() => handleSort('sizeBytes')}
                      >
                        Size
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'lastModifiedUtc'}
                        direction={sortField === 'lastModifiedUtc' ? sortOrder : 'asc'}
                        onClick={() => handleSort('lastModifiedUtc')}
                      >
                        Modified
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'createdUtc'}
                        direction={sortField === 'createdUtc' ? sortOrder : 'asc'}
                        onClick={() => handleSort('createdUtc')}
                      >
                        Created
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFiles.map((file) => (
                    <TableRow
                      key={file.fileName}
                      hover
                      selected={selectedFile === file.fileName}
                      onClick={() => handleFileSelect(file.fileName)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Description fontSize="small" color="action" />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {file.fileName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{file.sizeFormatted}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(file.lastModifiedUtc)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(file.createdUtc)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Box>
      </Drawer>

      {/* Content Panel */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedFile ? (
          <>
            {/* Toolbar */}
            <Paper sx={{ p: 2, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Tooltip title="Select log file">
                    <Button
                      variant="outlined"
                      startIcon={<FolderOpen />}
                      onClick={() => setDrawerOpen(true)}
                    >
                      Files
                    </Button>
                  </Tooltip>
                  <Box>
                    <Typography variant="h6">{selectedFile}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {totalLines.toLocaleString()} total lines
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="paginated">
                      <Tooltip title="Paginated view (oldest first)">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <FirstPage fontSize="small" />
                          Pages
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="tail">
                      <Tooltip title="Tail view (most recent)">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LastPage fontSize="small" />
                          Tail
                        </Box>
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={viewMode === 'paginated' ? loadContent : loadTail}
                    disabled={contentLoading}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {viewMode === 'paginated' ? (
                  <>
                    <TextField
                      size="small"
                      placeholder="Search in log..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      sx={{ width: 300 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: searchInput && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleSearch}>
                              <Search fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ width: 120 }}>
                      <InputLabel>Page Size</InputLabel>
                      <Select
                        value={pageSize}
                        label="Page Size"
                        onChange={(e) => {
                          setPageSize(Number(e.target.value))
                          setPage(1)
                        }}
                      >
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                      </Select>
                    </FormControl>
                    {searchTerm && (
                      <Chip
                        label={`Search: "${searchTerm}"`}
                        onDelete={() => {
                          setSearchTerm('')
                          setSearchInput('')
                        }}
                        size="small"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <FormControl size="small" sx={{ width: 150 }}>
                      <InputLabel>Lines to show</InputLabel>
                      <Select
                        value={tailLines}
                        label="Lines to show"
                        onChange={(e) => setTailLines(Number(e.target.value))}
                      >
                        <MenuItem value={25}>25 lines</MenuItem>
                        <MenuItem value={50}>50 lines</MenuItem>
                        <MenuItem value={100}>100 lines</MenuItem>
                        <MenuItem value={200}>200 lines</MenuItem>
                      </Select>
                    </FormControl>
                    <Button
                      variant={autoRefresh ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={autoRefresh ? <Stop /> : <PlayArrow />}
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      color={autoRefresh ? 'primary' : 'inherit'}
                    >
                      {autoRefresh ? 'Stop Auto-refresh' : 'Auto-refresh'}
                    </Button>
                    {autoRefresh && (
                      <Chip
                        label="Refreshing every 3s"
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </>
                )}
              </Box>
            </Paper>

            {/* Log Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                bgcolor: 'grey.900',
                p: 0,
              }}
            >
              {contentLoading && content.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : contentError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {contentError}
                </Alert>
              ) : content.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {searchTerm ? 'No matching lines found' : 'No log entries'}
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                  }}
                >
                  {content.map((line, index) => (
                    <Box
                      key={line.lineNumber || index}
                      sx={{
                        display: 'flex',
                        bgcolor: getLineBackground(line.content),
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          color: 'grey.600',
                          minWidth: 60,
                          textAlign: 'right',
                          pr: 2,
                          pl: 1,
                          userSelect: 'none',
                          borderRight: 1,
                          borderColor: 'grey.800',
                        }}
                      >
                        {line.lineNumber}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          color: 'grey.100',
                          pl: 2,
                          pr: 1,
                          flex: 1,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {line.content}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Pagination */}
            {viewMode === 'paginated' && totalPages > 1 && (
              <Paper sx={{ p: 2, borderRadius: 0, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="body2" color="text.secondary">
                    Page {page} of {totalPages}
                  </Typography>
                </Box>
              </Paper>
            )}
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a log file to view
              </Typography>
              <Button
                variant="contained"
                startIcon={<FolderOpen />}
                onClick={() => setDrawerOpen(true)}
                sx={{ mt: 2 }}
              >
                Browse Log Files
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}
